import { ProcessedFilters } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { getSearchParams } from '@app/pages/shop/utils/getSearchParams';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { OptimisticOption } from './types';
import { useFilters } from './useFilters';

interface Context {
  filterData: ProcessedFilters | null;
  handleOptionClick: (url: string, button: string, checked?: boolean) => void;
  optimisticOption?: OptimisticOption;
  loading: boolean;
  pathWithQueryString: string;
}

export const FiltersContext = createContext<Context | undefined>(undefined);

export function FiltersProvider({ filters, experience, children }) {
  const router = useRouter();
  const [{ locale, country, currency }] = useUserInfo<UserInfo>();
  const [resetUrl, setResetUrl] = useState<string>(router.asPath);
  const [pathWithQueryString, setPathWithQueryString] = useState<string>(
    router.asPath
  );

  const {
    filters: filterData,
    optimisticOption,
    loading,
    refresh,
  } = useFilters(filters, resetUrl);

  useEffect(() => {
    setResetUrl(router.asPath);
  }, [router.asPath]);

  const contextValue = useMemo(() => {
    const handleOptionClick = (
      url: string,
      button: string,
      checked: boolean
    ) => {
      const urlParams = new URLSearchParams(url.split('?')[1]);

      const dict: Record<string, string | string[]> = {};
      let query = '';

      const paramsArray = Array.from(urlParams.entries());

      paramsArray.forEach(([key, value]) => {
        const decodedKey = decodeURIComponent(key);
        const decodedValue = decodeURIComponent(value);

        if (decodedKey === 'query') {
          query = decodedValue;
        } else {
          const existingValue = dict[decodedKey];

          if (existingValue) {
            dict[decodedKey] = Array.isArray(existingValue)
              ? [...existingValue, decodedValue]
              : [existingValue, decodedValue];
          } else {
            dict[decodedKey] = decodedValue;
          }
        }
      });

      setPathWithQueryString(url);

      refresh(
        {
          query,
          queryParams: getSearchParams(dict),
          locale,
          country,
          currency,
          experience,
        },
        url,
        checked,
        button
      );
    };

    return {
      filterData,
      handleOptionClick,
      optimisticOption,
      loading,
      pathWithQueryString,
    };
  }, [
    filterData,
    optimisticOption,
    loading,
    pathWithQueryString,
    refresh,
    locale,
    country,
    currency,
    experience,
  ]);

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
}

export function useFiltersContext() {
  const context = useContext(FiltersContext);

  if (context === undefined) {
    throw new Error(
      'useFiltersContext must be used within a FiltersContext provider'
    );
  }

  return context;
}
